import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Plus, Edit, Trash2, Copy, ToggleLeft, ToggleRight,
  Save, X, ChevronDown, ChevronRight, FileText, Code, 
  CheckSquare, DollarSign, Clock, AlertTriangle, Download,
  Upload, RefreshCw, Search, Filter, Crown, Shield, Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier badge component
const TierBadge = ({ tier }) => {
  const config = {
    basic: { bg: 'bg-zinc-700', text: 'text-zinc-300', label: 'Basic' },
    professional: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Professional' },
    elite: { bg: 'bg-[#D4AF37]/20', text: 'text-[#D4AF37]', label: 'Elite' },
  };
  const c = config[tier] || config.basic;
  return (
    <span className={`px-2 py-1 text-xs font-bold uppercase ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

// Task list item component
const TaskListItem = ({ task, onEdit, onDelete, onToggle, onClone }) => (
  <div className={`bg-zinc-900 border ${task.is_active ? 'border-zinc-700' : 'border-red-500/30'} p-4 hover:border-[#D4AF37]/50 transition-colors`}>
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-white font-bold">{task.task_name}</span>
          <TierBadge tier={task.tier} />
          {!task.is_active && (
            <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400">INACTIVE</span>
          )}
        </div>
        <div className="flex items-center gap-4 text-zinc-500 text-sm">
          <span>ID: <code className="text-[#D4AF37]">{task.task_id}</code></span>
          <span>Phase {task.phase}</span>
          {task.day_in_roadmap && <span>Day {task.day_in_roadmap}</span>}
          <span>v{task.version}</span>
        </div>
        <p className="text-zinc-600 text-xs mt-1">
          Updated: {new Date(task.updated_at).toLocaleString()}
        </p>
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={() => onToggle(task.task_id)}
          className={`p-2 ${task.is_active ? 'text-green-400 hover:text-green-300' : 'text-zinc-500 hover:text-zinc-400'}`}
          title={task.is_active ? 'Deactivate' : 'Activate'}
        >
          {task.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
        </button>
        <button
          onClick={() => onClone(task.task_id)}
          className="p-2 text-zinc-500 hover:text-blue-400"
          title="Clone"
        >
          <Copy className="w-5 h-5" />
        </button>
        <button
          onClick={() => onEdit(task.task_id)}
          className="p-2 text-zinc-500 hover:text-[#D4AF37]"
          title="Edit"
        >
          <Edit className="w-5 h-5" />
        </button>
        <button
          onClick={() => onDelete(task.task_id)}
          className="p-2 text-zinc-500 hover:text-red-400"
          title="Delete"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  </div>
);

// Task editor modal component
const TaskEditor = ({ taskId, onClose, onSave }) => {
  const [task, setTask] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTask = async () => {
      if (!taskId) {
        // New task
        setTask({
          task_id: '',
          task_name: '',
          phase: 1,
          tier: 'basic',
          day_in_roadmap: null,
          is_active: true,
          strategic_overview: {
            why_it_matters: '',
            long_term_impact: '',
            compliance_connection: '',
            common_mistakes: [],
            cost_of_failure: ''
          },
          execution_steps: [],
          scripts: [],
          required_documents: [],
          checklists: { pre_filing: [], post_filing: [] },
          downloadable_files: [],
          cost_breakdown: [],
          total_estimated_cost: 0,
          total_estimated_time: '',
          proof_requirements: []
        });
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        const res = await axios.get(`${API}/build-cms/tasks/${taskId}`);
        setTask(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load task');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTask();
  }, [taskId]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (taskId) {
        await axios.put(`${API}/build-cms/tasks/${taskId}`, task, { headers });
      } else {
        await axios.post(`${API}/build-cms/tasks`, task, { headers });
      }
      
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save task');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field, value) => {
    setTask(prev => ({ ...prev, [field]: value }));
  };

  const updateOverview = (field, value) => {
    setTask(prev => ({
      ...prev,
      strategic_overview: { ...prev.strategic_overview, [field]: value }
    }));
  };

  const addExecutionStep = () => {
    const newStep = {
      step_number: (task.execution_steps?.length || 0) + 1,
      title: '',
      description: '',
      url: '',
      field_guidance: [],
      warnings: [],
      tips: []
    };
    setTask(prev => ({
      ...prev,
      execution_steps: [...(prev.execution_steps || []), newStep]
    }));
  };

  const updateStep = (index, field, value) => {
    const steps = [...task.execution_steps];
    steps[index] = { ...steps[index], [field]: value };
    setTask(prev => ({ ...prev, execution_steps: steps }));
  };

  const removeStep = (index) => {
    const steps = task.execution_steps.filter((_, i) => i !== index);
    // Renumber steps
    steps.forEach((step, i) => { step.step_number = i + 1; });
    setTask(prev => ({ ...prev, execution_steps: steps }));
  };

  const addScript = () => {
    const newScript = {
      script_type: 'phone',
      title: '',
      scenario: '',
      script_text: '',
      variables: []
    };
    setTask(prev => ({
      ...prev,
      scripts: [...(prev.scripts || []), newScript]
    }));
  };

  const updateScript = (index, field, value) => {
    const scripts = [...task.scripts];
    scripts[index] = { ...scripts[index], [field]: value };
    setTask(prev => ({ ...prev, scripts }));
  };

  const removeScript = (index) => {
    setTask(prev => ({
      ...prev,
      scripts: prev.scripts.filter((_, i) => i !== index)
    }));
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'steps', label: 'Execution Steps', icon: CheckSquare },
    { id: 'scripts', label: 'Scripts', icon: Code },
    { id: 'checklists', label: 'Checklists', icon: CheckSquare },
    { id: 'costs', label: 'Costs & Time', icon: DollarSign },
  ];

  return (
    <div className="fixed inset-0 bg-black/90 overflow-y-auto z-50">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={onClose} className="text-zinc-500 hover:text-white">
              <X className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-white">
              {taskId ? 'Edit Task' : 'New Task'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-zinc-800 text-white hover:bg-zinc-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 bg-[#D4AF37] text-black font-bold hover:bg-[#F5D47B] disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving...' : 'Save Task'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 text-red-400">
            {error}
          </div>
        )}

        {/* Basic Info */}
        <div className="bg-zinc-900 border border-zinc-700 p-6 mb-6">
          <h2 className="text-white font-bold mb-4">Basic Information</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-zinc-500 text-sm block mb-1">Task ID*</label>
              <input
                type="text"
                value={task?.task_id || ''}
                onChange={(e) => updateField('task_id', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                disabled={!!taskId}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white disabled:opacity-50"
                placeholder="llc_guidance"
              />
            </div>
            <div>
              <label className="text-zinc-500 text-sm block mb-1">Task Name*</label>
              <input
                type="text"
                value={task?.task_name || ''}
                onChange={(e) => updateField('task_name', e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white"
                placeholder="LLC Formation Guidance"
              />
            </div>
            <div>
              <label className="text-zinc-500 text-sm block mb-1">Phase (1-5)*</label>
              <select
                value={task?.phase || 1}
                onChange={(e) => updateField('phase', parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white"
              >
                {[1, 2, 3, 4, 5].map(p => (
                  <option key={p} value={p}>Phase {p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-zinc-500 text-sm block mb-1">Tier*</label>
              <select
                value={task?.tier || 'basic'}
                onChange={(e) => updateField('tier', e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white"
              >
                <option value="basic">Basic ($997)</option>
                <option value="professional">Professional ($1,997)</option>
                <option value="elite">Elite ($2,997)</option>
              </select>
            </div>
            <div>
              <label className="text-zinc-500 text-sm block mb-1">Day in Roadmap</label>
              <input
                type="number"
                value={task?.day_in_roadmap || ''}
                onChange={(e) => updateField('day_in_roadmap', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white"
                placeholder="1-30"
                min="1"
                max="30"
              />
            </div>
            <div>
              <label className="text-zinc-500 text-sm block mb-1">Total Est. Cost ($)</label>
              <input
                type="number"
                value={task?.total_estimated_cost || 0}
                onChange={(e) => updateField('total_estimated_cost', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white"
              />
            </div>
            <div>
              <label className="text-zinc-500 text-sm block mb-1">Total Est. Time</label>
              <input
                type="text"
                value={task?.total_estimated_time || ''}
                onChange={(e) => updateField('total_estimated_time', e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white"
                placeholder="3-5 business days"
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                checked={task?.is_active ?? true}
                onChange={(e) => updateField('is_active', e.target.checked)}
                className="w-4 h-4"
              />
              <label className="text-white">Active</label>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 flex items-center gap-2 whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-[#D4AF37] text-black font-bold' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-zinc-900 border border-zinc-700 p-6">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <h3 className="text-white font-bold">Strategic Overview</h3>
              <div>
                <label className="text-zinc-500 text-sm block mb-1">Why This Step Matters</label>
                <textarea
                  value={task?.strategic_overview?.why_it_matters || ''}
                  onChange={(e) => updateOverview('why_it_matters', e.target.value)}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white h-24"
                  placeholder="Explain why this step is critical..."
                />
              </div>
              <div>
                <label className="text-zinc-500 text-sm block mb-1">Long-Term Impact</label>
                <textarea
                  value={task?.strategic_overview?.long_term_impact || ''}
                  onChange={(e) => updateOverview('long_term_impact', e.target.value)}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white h-24"
                />
              </div>
              <div>
                <label className="text-zinc-500 text-sm block mb-1">Compliance Connection</label>
                <textarea
                  value={task?.strategic_overview?.compliance_connection || ''}
                  onChange={(e) => updateOverview('compliance_connection', e.target.value)}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white h-24"
                />
              </div>
              <div>
                <label className="text-zinc-500 text-sm block mb-1">Common Mistakes (one per line)</label>
                <textarea
                  value={(task?.strategic_overview?.common_mistakes || []).join('\n')}
                  onChange={(e) => updateOverview('common_mistakes', e.target.value.split('\n').filter(m => m.trim()))}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white h-32"
                  placeholder="Filing in the wrong state
Using personal SSN instead of EIN"
                />
              </div>
              <div>
                <label className="text-zinc-500 text-sm block mb-1">Cost of Getting This Wrong</label>
                <textarea
                  value={task?.strategic_overview?.cost_of_failure || ''}
                  onChange={(e) => updateOverview('cost_of_failure', e.target.value)}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white h-24"
                />
              </div>
            </div>
          )}

          {activeTab === 'steps' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-bold">Execution Steps ({task?.execution_steps?.length || 0})</h3>
                <button
                  onClick={addExecutionStep}
                  className="px-4 py-2 bg-[#D4AF37] text-black font-bold flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Step
                </button>
              </div>
              
              {task?.execution_steps?.map((step, index) => (
                <div key={index} className="bg-zinc-800 border border-zinc-600 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[#D4AF37] font-bold">Step {step.step_number}</span>
                    <button
                      onClick={() => removeStep(index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="text-zinc-500 text-xs block mb-1">Title</label>
                      <input
                        type="text"
                        value={step.title || ''}
                        onChange={(e) => updateStep(index, 'title', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-500 text-xs block mb-1">URL (optional)</label>
                      <input
                        type="text"
                        value={step.url || ''}
                        onChange={(e) => updateStep(index, 'url', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="text-zinc-500 text-xs block mb-1">Description</label>
                    <textarea
                      value={step.description || ''}
                      onChange={(e) => updateStep(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white h-20"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-zinc-500 text-xs block mb-1">Warnings (one per line)</label>
                      <textarea
                        value={(step.warnings || []).join('\n')}
                        onChange={(e) => updateStep(index, 'warnings', e.target.value.split('\n').filter(w => w.trim()))}
                        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white h-20 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-500 text-xs block mb-1">Tips (one per line)</label>
                      <textarea
                        value={(step.tips || []).join('\n')}
                        onChange={(e) => updateStep(index, 'tips', e.target.value.split('\n').filter(t => t.trim()))}
                        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white h-20 text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'scripts' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-bold">Script Vault ({task?.scripts?.length || 0})</h3>
                <button
                  onClick={addScript}
                  className="px-4 py-2 bg-[#D4AF37] text-black font-bold flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Script
                </button>
              </div>
              
              {task?.scripts?.map((script, index) => (
                <div key={index} className="bg-zinc-800 border border-zinc-600 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <select
                      value={script.script_type || 'phone'}
                      onChange={(e) => updateScript(index, 'script_type', e.target.value)}
                      className="px-3 py-1 bg-zinc-900 border border-zinc-700 text-white"
                    >
                      <option value="phone">Phone Script</option>
                      <option value="email">Email Template</option>
                      <option value="follow_up">Follow-Up Script</option>
                    </select>
                    <button
                      onClick={() => removeScript(index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="text-zinc-500 text-xs block mb-1">Title</label>
                      <input
                        type="text"
                        value={script.title || ''}
                        onChange={(e) => updateScript(index, 'title', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-500 text-xs block mb-1">Scenario</label>
                      <input
                        type="text"
                        value={script.scenario || ''}
                        onChange={(e) => updateScript(index, 'scenario', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-zinc-500 text-xs block mb-1">Script Text</label>
                    <textarea
                      value={script.script_text || ''}
                      onChange={(e) => updateScript(index, 'script_text', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white h-32 font-mono text-sm"
                      placeholder="Hi, my name is [YOUR NAME]..."
                    />
                  </div>
                  <p className="text-zinc-600 text-xs mt-2">
                    Use [BRACKETS] for variables like [YOUR NAME], [COMPANY], [DATE]
                  </p>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'checklists' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-white font-bold mb-3">Pre-Filing Checklist</h3>
                <textarea
                  value={(task?.checklists?.pre_filing || []).map(i => `${i.required ? '*' : ''}${i.text}`).join('\n')}
                  onChange={(e) => {
                    const items = e.target.value.split('\n').filter(l => l.trim()).map((line, idx) => ({
                      id: `pf${idx + 1}`,
                      text: line.replace(/^\*/, ''),
                      required: line.startsWith('*'),
                      proof_required: false
                    }));
                    setTask(prev => ({
                      ...prev,
                      checklists: { ...prev.checklists, pre_filing: items }
                    }));
                  }}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white h-32"
                  placeholder="*Required item (prefix with *)
Optional item"
                />
                <p className="text-zinc-600 text-xs mt-1">Prefix with * for required items</p>
              </div>
              <div>
                <h3 className="text-white font-bold mb-3">Post-Filing Checklist</h3>
                <textarea
                  value={(task?.checklists?.post_filing || []).map(i => `${i.required ? '*' : ''}${i.text}`).join('\n')}
                  onChange={(e) => {
                    const items = e.target.value.split('\n').filter(l => l.trim()).map((line, idx) => ({
                      id: `pof${idx + 1}`,
                      text: line.replace(/^\*/, ''),
                      required: line.startsWith('*'),
                      proof_required: true,
                      proof_type: 'document'
                    }));
                    setTask(prev => ({
                      ...prev,
                      checklists: { ...prev.checklists, post_filing: items }
                    }));
                  }}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white h-32"
                  placeholder="*Saved confirmation number
*Downloaded certificate"
                />
              </div>
              <div>
                <h3 className="text-white font-bold mb-3">Required Documents (one per line)</h3>
                <textarea
                  value={(task?.required_documents || []).join('\n')}
                  onChange={(e) => updateField('required_documents', e.target.value.split('\n').filter(d => d.trim()))}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white h-24"
                  placeholder="Government-issued photo ID
Social Security Number"
                />
              </div>
            </div>
          )}

          {activeTab === 'costs' && (
            <div className="space-y-4">
              <h3 className="text-white font-bold">Cost Breakdown</h3>
              <p className="text-zinc-500 text-sm">Edit costs directly in JSON format:</p>
              <textarea
                value={JSON.stringify(task?.cost_breakdown || [], null, 2)}
                onChange={(e) => {
                  try {
                    const costs = JSON.parse(e.target.value);
                    updateField('cost_breakdown', costs);
                  } catch (err) {
                    // Invalid JSON, don't update
                  }
                }}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white font-mono text-sm h-64"
              />
              <p className="text-zinc-600 text-xs">
                Format: {"[{\"item\": \"Filing Fee\", \"government_fee\": 300, \"service_fee\": 0, \"total_cost\": 300, \"processing_time\": \"3-5 days\"}]"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Admin CMS Page
const BuildCMSPage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [filterPhase, setFilterPhase] = useState(null);
  const [filterTier, setFilterTier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTasks = async () => {
    try {
      setIsLoading(true);
      let url = `${API}/build-cms/tasks`;
      const params = new URLSearchParams();
      if (filterPhase) params.append('phase', filterPhase);
      if (filterTier) params.append('tier', filterTier);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url);
      setTasks(res.data);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [filterPhase, filterTier]);

  const handleEdit = (taskId) => {
    setEditingTaskId(taskId);
    setShowEditor(true);
  };

  const handleCreate = () => {
    setEditingTaskId(null);
    setShowEditor(true);
  };

  const handleDelete = async (taskId) => {
    if (!confirm(`Delete task "${taskId}"? This cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/build-cms/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete task');
    }
  };

  const handleToggle = async (taskId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(`${API}/build-cms/tasks/${taskId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to toggle task');
    }
  };

  const handleClone = async (taskId) => {
    const newId = prompt('Enter new task ID:', `${taskId}_copy`);
    if (!newId) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/build-cms/tasks/${taskId}/clone?new_task_id=${newId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to clone task');
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/build-cms/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `build-content-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    } catch (err) {
      alert('Failed to export');
    }
  };

  const handleSeedDefaults = async () => {
    if (!confirm('Seed default Foundation tier content? This will add predefined tasks.')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${API}/build-cms/seed-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Seeded: ${res.data.seeded}, Skipped: ${res.data.skipped}`);
      fetchTasks();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to seed');
    }
  };

  const filteredTasks = tasks.filter(task => 
    !searchTerm || 
    task.task_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    task.task_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group tasks by phase
  const tasksByPhase = {};
  for (let p = 1; p <= 5; p++) {
    tasksByPhase[p] = filteredTasks.filter(t => t.phase === p);
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="build-cms-page">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 px-6 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="text-zinc-500 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">Build Content CMS</h1>
              <p className="text-zinc-500 text-sm">Manage task instructions, scripts, and checklists</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleSeedDefaults}
              className="px-4 py-2 bg-zinc-800 text-zinc-400 hover:text-white flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Seed Defaults
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-zinc-800 text-zinc-400 hover:text-white flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={handleCreate}
              className="px-4 py-2 bg-[#D4AF37] text-black font-bold flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Task
            </button>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-700 text-white w-64"
            />
          </div>
          <select
            value={filterPhase || ''}
            onChange={(e) => setFilterPhase(e.target.value ? parseInt(e.target.value) : null)}
            className="px-4 py-2 bg-zinc-900 border border-zinc-700 text-white"
          >
            <option value="">All Phases</option>
            {[1, 2, 3, 4, 5].map(p => (
              <option key={p} value={p}>Phase {p}</option>
            ))}
          </select>
          <select
            value={filterTier || ''}
            onChange={(e) => setFilterTier(e.target.value || null)}
            className="px-4 py-2 bg-zinc-900 border border-zinc-700 text-white"
          >
            <option value="">All Tiers</option>
            <option value="basic">Basic</option>
            <option value="professional">Professional</option>
            <option value="elite">Elite</option>
          </select>
          <span className="text-zinc-500 text-sm">
            {filteredTasks.length} tasks
          </span>
        </div>
      </div>

      {/* Task List */}
      <main className="max-w-7xl mx-auto px-6 pb-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl text-white font-bold mb-2">No Tasks Found</h3>
            <p className="text-zinc-500 mb-4">Click "Seed Defaults" to add Foundation tier content</p>
            <button
              onClick={handleCreate}
              className="px-6 py-3 bg-[#D4AF37] text-black font-bold"
            >
              Create First Task
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {[1, 2, 3, 4, 5].map(phase => (
              tasksByPhase[phase]?.length > 0 && (
                <div key={phase}>
                  <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 bg-[#D4AF37] text-black flex items-center justify-center font-bold">
                      {phase}
                    </span>
                    Phase {phase}
                    <span className="text-zinc-500 font-normal">({tasksByPhase[phase].length} tasks)</span>
                  </h2>
                  <div className="space-y-2">
                    {tasksByPhase[phase].map(task => (
                      <TaskListItem
                        key={task.task_id}
                        task={task}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggle={handleToggle}
                        onClone={handleClone}
                      />
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </main>

      {/* Task Editor Modal */}
      {showEditor && (
        <TaskEditor
          taskId={editingTaskId}
          onClose={() => setShowEditor(false)}
          onSave={() => {
            setShowEditor(false);
            fetchTasks();
          }}
        />
      )}
    </div>
  );
};

export default BuildCMSPage;
